import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Container,
  Stack,
  Text,
  Title,
  Grid,
  Card,
  Group,
  ThemeIcon,
  Button,
  Alert,
  Skeleton,
  Paper,
  List,
} from "@mantine/core";
import {
  IconPhone,
  IconMail,
  IconAlertCircle,
  IconCircleCheck,
  IconHelp,
} from "@tabler/icons-react";
import { physicianPortalService } from "../../services/physicianPortal.service";

export function PortalContactHelpPage() {
  const {
    data: config,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["portal", "contact-config"],
    queryFn: () => physicianPortalService.getContactConfig(),
    staleTime: 5 * 60 * 1000,
  });

  const emailHref = useMemo(() => {
    if (!config?.supportEmail) return "";
    return `mailto:${config.supportEmail}`;
  }, [config?.supportEmail]);

  const phoneHref = useMemo(() => {
    if (!config?.supportPhone) return "";
    // Clean any non-numeric characters for tel link, except '+'
    const cleaned = config.supportPhone.replace(/[^\d+]/g, "");
    return `tel:${cleaned}`;
  }, [config?.supportPhone]);

  const supportCategories = useMemo(() => {
    return config?.supportCategories ?? [];
  }, [config?.supportCategories]);

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up {
          animation: fadeUp 0.35s ease forwards;
        }
      `}</style>

      <Container size="lg" py="lg" className="fade-up">
        <Stack gap="xl">
          {/* Page header */}
          <Paper
            py="xl"
            px={0}
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-2)",
              background: "transparent",
            }}
            radius={0}
          >
            <Stack gap={4}>
              <Text
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--mantine-color-teal-6)",
                }}
              >
                Physician Portal
              </Text>
              <Title
                order={1}
                c="blue.9"
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontWeight: 400,
                  fontSize: 34,
                }}
              >
                Contact & Support
              </Title>
            </Stack>
          </Paper>

          {/* Loading state */}
          {isLoading && (
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="md">
                  <Skeleton height={120} radius="md" />
                  <Skeleton height={120} radius="md" />
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="md">
                  <Skeleton height={180} radius="md" />
                  <Skeleton height={150} radius="md" />
                </Stack>
              </Grid.Col>
            </Grid>
          )}

          {/* Error state */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Unable to load support information"
              role="alert"
            >
              We are unable to load the contact configuration at this time.
              Please verify your internet connection or try refreshing the page.
            </Alert>
          )}

          {/* Main content when loaded */}
          {!isLoading && !error && config && (
            <Grid gutter={32}>
              {/* Direct Contact Methods */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="lg">
                  <Text
                    c="dark.6"
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  >
                    Direct Contact
                  </Text>
                  <Text size="sm" c="dimmed">
                    Our support team is available to assist you with any
                    questions regarding cases, reports, or portal access. Feel
                    free to reach out via email or phone.
                  </Text>

                  {/* Email Card */}
                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Group gap="md">
                      <ThemeIcon size="xl" radius="md" color="teal">
                        <IconMail size={24} />
                      </ThemeIcon>
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text size="sm" fw={600} c="dimmed">
                          EMAIL SUPPORT
                        </Text>
                        <Text size="lg" fw={500}>
                          {config.supportEmail}
                        </Text>
                      </Stack>
                    </Group>
                    <Button
                      component="a"
                      href={emailHref}
                      variant="light"
                      color="teal"
                      fullWidth
                      mt="md"
                      leftSection={<IconMail size={16} />}
                    >
                      Send Email
                    </Button>
                  </Card>

                  {/* Phone Card (Only render if supportPhone is provided) */}
                  {config.supportPhone && (
                    <Card shadow="sm" padding="lg" radius="md" withBorder>
                      <Group gap="md">
                        <ThemeIcon size="xl" radius="md" color="indigo">
                          <IconPhone size={24} />
                        </ThemeIcon>
                        <Stack gap={2} style={{ flex: 1 }}>
                          <Text size="sm" fw={600} c="dimmed">
                            PHONE SUPPORT
                          </Text>
                          <Text size="lg" fw={500}>
                            {config.supportPhone}
                          </Text>
                        </Stack>
                      </Group>
                      <Button
                        component="a"
                        href={phoneHref}
                        variant="light"
                        color="indigo"
                        fullWidth
                        mt="md"
                        leftSection={<IconPhone size={16} />}
                      >
                        Call Us
                      </Button>
                    </Card>
                  )}
                </Stack>
              </Grid.Col>

              {/* Guidance & Categories */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="xl">
                  {/* Missing Case Guidance Section */}
                  {config.missingCaseGuidance && (
                    <Stack gap="md">
                      <Text
                        c="dark.6"
                        style={{
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          fontSize: 18,
                          fontWeight: 600,
                        }}
                      >
                        Missing Case Guidance
                      </Text>
                      <Alert
                        color="blue"
                        icon={<IconHelp size={20} />}
                        radius="md"
                        style={{
                          borderLeft: "4px solid var(--mantine-color-blue-6)",
                        }}
                      >
                        <Text
                          size="sm"
                          style={{ whiteSpace: "pre-line", lineHeight: 1.5 }}
                        >
                          {config.missingCaseGuidance}
                        </Text>
                      </Alert>
                    </Stack>
                  )}

                  {/* Common Support Categories */}
                  {supportCategories.length > 0 && (
                    <Stack gap="md">
                      <Text
                        c="dark.6"
                        style={{
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          fontSize: 18,
                          fontWeight: 600,
                        }}
                      >
                        Common Support Areas
                      </Text>
                      <Text size="sm" c="dimmed">
                        We can help you resolve issues related to any of the
                        following categories:
                      </Text>
                      <Paper withBorder radius="md" bg="gray.0" p="md">
                        <List
                          spacing="sm"
                          size="sm"
                          center
                          icon={
                            <ThemeIcon color="teal" size={20} radius="xl">
                              <IconCircleCheck size={12} />
                            </ThemeIcon>
                          }
                        >
                          {supportCategories.map((cat, idx) => (
                            <List.Item key={idx}>
                              <Text size="sm" fw={500}>
                                {cat}
                              </Text>
                            </List.Item>
                          ))}
                        </List>
                      </Paper>
                    </Stack>
                  )}
                </Stack>
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Container>
    </>
  );
}

export default PortalContactHelpPage;
